import React, { useState, useEffect, useRef } from "react"
import Header from "../../../layouts/Header"
import BackIcon from "../../../components/BackIcon"
import { useNavigate } from "react-router-dom"
import {
  Box,
  Typography,
  Button,
  Grid,
  Alert,
  CircularProgress,
  useMediaQuery,
  useTheme,
  LinearProgress,
} from "@mui/material"
import { ArrowBack, LocationOn, MyLocation, CheckCircle } from "@mui/icons-material"
import { createTheme, ThemeProvider } from "@mui/material/styles"
import { DatePicker } from "@mui/x-date-pickers/DatePicker"
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider"
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns"
import esLocale from "date-fns/locale/es"
import { addYears } from "date-fns"
import { useQuery, useMutation } from "@tanstack/react-query"
import fetchwrapper from "../../../services/interceptors/fetchwrapper"
import CustomBackdrop from "../../../components/CustomBackdrop"
import Swal from "sweetalert2"
import CustomPhotoCam from "../../../components/CustomPhotoCam"

// Componentes modulares
import Stepper from "../components/Stepper"
import EventoCard from "../components/EventoCard"
import MapaUbicacion from "../components/MapaUbicacion"

// =============================================
// CONSTANTES GLOBALES Y CONFIGURACIÓN
// =============================================

// Configuración de geolocalización
const GEOLOCATION_CONFIG = {
  MAX_PRECISION_UBICACION: 3000, // metros - máxima precisión aceptable
  PRECISION_OBJETIVO: 3000, // metros - precisión considerada buena
  TIMEOUT: 30000, // 30 segundos máximo de espera
  OPCIONES_GEOLOCALIZACION: {
    enableHighAccuracy: true,
    timeout: 30000,
    maximumAge: 0, // No usar posiciones cacheadas
  },
}

// Configuración de la cámara
const CAMERA_CONFIG = {
  COMPRESION: {
    maxSizeMB: 0.5,
    maxWidthOrHeight: 300,
    useWebWorker: true,
  },
  ASPECT_RATIO: 1,
}

// Configuración de temas
const APP_THEME = createTheme({
  palette: {
    primary: {
      main: "#196C87",
    },
    secondary: {
      main: "#2E7D32",
    },
    info: {
      main: "#0288D1",
    },
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 900,
      lg: 1200,
      xl: 1536,
    },
  },
})

// Estilos para el contenedor principal
const STYLED_ROOT = {
  width: "100%",
  maxWidth: "1200px",
  margin: "0 auto",
  padding: "20px",
  backgroundColor: "#f5f7fa",
  borderRadius: "12px",
  boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
}

// Mensajes de error para geolocalización
const ERROR_MESSAGES = {
  PERMISSION_DENIED: "Permiso de ubicación denegado. Por favor, habilita los permisos de ubicación en tu navegador.",
  POSITION_UNAVAILABLE: "Información de ubicación no disponible. Verifica tu conexión y GPS.",
  TIMEOUT: "Tiempo de espera agotado al obtener la ubicación. Intenta en un lugar con mejor recepción.",
  DEFAULT: "Error al obtener la ubicación",
  NO_GEOLOCATION_SUPPORT: "Tu navegador no soporta geolocalización",
}

// =============================================
// COMPONENTE PRINCIPAL
// =============================================

const CrearEjecucionLabores = () => {
  const navigate = useNavigate()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"))
  const [eventos, setEventos] = useState([])
  const [eventoSeleccionado, setEventoSeleccionado] = useState(null)
  const [foto, setFoto] = useState(null)
  const [ubicacion, setUbicacion] = useState(null)
  const [cargandoUbicacion, setCargandoUbicacion] = useState(false)
  const [errorUbicacion, setErrorUbicacion] = useState(null)
  const [pasoActual, setPasoActual] = useState(1) // 1: Seleccionar evento, 2: Capturar foto, 3: Confirmar
  const [enviando, setEnviando] = useState(false)
  const [mejorPrecision, setMejorPrecision] = useState(null)
  const watchIdRef = useRef(null)
  const timeoutRef = useRef(null)
  const [isLoadingEventos, setIsLoadingEventos] = useState(false)

  useEffect(() => {
    const getEventos = async () => {
      try {
        setIsLoadingEventos(true)
        const response = await fetchwrapper(`/EjecucionLabores/getAllHorariosPlanificadosByEmpleado`)
        const result = await response.json()
        setEventos(result.data)
      } catch (error) {
        alert("Error de cargado eventos")
      } finally {
        setIsLoadingEventos(false)
      }
    }

    getEventos()

    // Limpiar al desmontar
    return () => {
      if (watchIdRef.current) {
        navigator.geolocation.clearWatch(watchIdRef.current)
      }
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [])

  // Obtener ubicación actual con alta precisión
  const obtenerUbicacion = () => {
    setCargandoUbicacion(true)
    setErrorUbicacion(null)
    setMejorPrecision(null)

    if (!navigator.geolocation) {
      setErrorUbicacion(ERROR_MESSAGES.NO_GEOLOCATION_SUPPORT)
      setCargandoUbicacion(false)
      return
    }

    // Primero intentamos con getCurrentPosition
    navigator.geolocation.getCurrentPosition(
      (position) => {
        verificarYEstablecerPrecision(position)
      },
      (error) => {
        manejarErrorGeolocalizacion(error)
      },
      GEOLOCATION_CONFIG.OPCIONES_GEOLOCALIZACION,
    )

    // También iniciamos un watcher para obtener actualizaciones de posición
    if (watchIdRef.current) {
      navigator.geolocation.clearWatch(watchIdRef.current)
    }

    watchIdRef.current = navigator.geolocation.watchPosition(
      (position) => {
        verificarYEstablecerPrecision(position)
      },
      (error) => {
        manejarErrorGeolocalizacion(error)
      },
      GEOLOCATION_CONFIG.OPCIONES_GEOLOCALIZACION,
    )

    // Establecer timeout para evitar esperas infinitas
    timeoutRef.current = setTimeout(() => {
      if (watchIdRef.current) {
        navigator.geolocation.clearWatch(watchIdRef.current)
        watchIdRef.current = null
      }

      if (!ubicacion && cargandoUbicacion) {
        setCargandoUbicacion(false)
        if (mejorPrecision) {
          // Usar la mejor precisión obtenida aunque no sea ideal
          setUbicacion(mejorPrecision)
          Swal.fire({
            title: "Precisión limitada",
            text: "No se pudo obtener una alta precisión. Se usará la mejor ubicación disponible.",
            icon: "warning",
            confirmButtonText: "Entendido",
          })
        } else {
          setErrorUbicacion("No se pudo obtener una ubicación precisa. Intenta en un lugar con mejor recepción de GPS.")
        }
      }
    }, GEOLOCATION_CONFIG.TIMEOUT)
  }

  // Verificar si la precisión es aceptable
  const verificarYEstablecerPrecision = (position) => {
    const { latitude, longitude, accuracy } = position.coords
    const nuevaUbicacion = {
      latitud: latitude,
      longitud: longitude,
      precision: accuracy,
      timestamp: new Date().toISOString(),
    }

    // Guardar la mejor precisión obtenida hasta ahora
    if (!mejorPrecision || accuracy < mejorPrecision.precision) {
      setMejorPrecision(nuevaUbicacion)
    }

    // Si la precisión es menor al objetivo, la consideramos buena
    if (accuracy <= GEOLOCATION_CONFIG.PRECISION_OBJETIVO) {
      if (watchIdRef.current) {
        navigator.geolocation.clearWatch(watchIdRef.current)
        watchIdRef.current = null
      }
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
        timeoutRef.current = null
      }

      setUbicacion(nuevaUbicacion)
      setCargandoUbicacion(false)
    }
  }

  // Manejar errores de geolocalización
  const manejarErrorGeolocalizacion = (error) => {
    let mensaje = ERROR_MESSAGES.DEFAULT

    switch (error.code) {
      case error.PERMISSION_DENIED:
        mensaje = ERROR_MESSAGES.PERMISSION_DENIED
        break
      case error.POSITION_UNAVAILABLE:
        mensaje = ERROR_MESSAGES.POSITION_UNAVAILABLE
        break
      case error.TIMEOUT:
        // No mostrar error si ya tenemos una ubicación
        if (mejorPrecision) {
          setUbicacion(mejorPrecision)
          setCargandoUbicacion(false)
          Swal.fire({
            title: "Precisión limitada",
            text: "No se pudo obtener una alta precisión. Se usará la mejor ubicación disponible.",
            icon: "warning",
            confirmButtonText: "Entendido",
          })
          return
        }
        mensaje = ERROR_MESSAGES.TIMEOUT
        break
      default:
        mensaje = ERROR_MESSAGES.DEFAULT
    }

    if (watchIdRef.current) {
      navigator.geolocation.clearWatch(watchIdRef.current)
      watchIdRef.current = null
    }
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }

    setErrorUbicacion(mensaje)
    setCargandoUbicacion(false)
  }

  // Manejar selección de evento
  const seleccionarEvento = (evento) => {
    setEventoSeleccionado(evento)
    setPasoActual(2)
  }

  // Manejar captura de foto
  const manejarFotoCapturada = (img) => {
    setFoto(img)
    setPasoActual(3)
    obtenerUbicacion()
  }

  // NUEVA FUNCIÓN: Saltar foto para supervisores
  const saltarFoto = () => {
    if (eventoSeleccionado?.extendedProps?.emcodemp) {
      // Si es supervisor
      setFoto(null) // Imagen nula
      setPasoActual(3)
      obtenerUbicacion()
    }
  }

  // Volver al paso anterior
  const volverAtras = () => {
    if (pasoActual === 2) {
      setPasoActual(1)
      setEventoSeleccionado(null)
    } else if (pasoActual === 3) {
      setPasoActual(2)
      setUbicacion(null)
      setErrorUbicacion(null)
      setMejorPrecision(null)

      // Limpiar watchers de geolocalización
      if (watchIdRef.current) {
        navigator.geolocation.clearWatch(watchIdRef.current)
        watchIdRef.current = null
      }
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
        timeoutRef.current = null
      }
    }
  }

  // Enviar asistencia
  const enviarAsistencia = async () => {
    // Ya no requiere foto obligatoria para supervisores
    if (!eventoSeleccionado || !ubicacion) {
      Swal.fire("Error", "Faltan datos para registrar la asistencia", "error")
      return
    }

    // Si NO es supervisor, validar que tenga foto
    if (!eventoSeleccionado?.extendedProps?.emcodemp && !foto) {
      Swal.fire("Error", "Debes capturar tu rostro", "error")
      return
    }

    setEnviando(true)

    try {
      const datosAsistencia = {
        isSupervisor: eventoSeleccionado.extendedProps.isSupervisor,
        emcodemp: eventoSeleccionado.extendedProps.emcodemp,
        loccodigo: eventoSeleccionado.extendedProps.loccodigo,
        planfecha: eventoSeleccionado.extendedProps.planfecha,
        horcodigo: eventoSeleccionado.extendedProps.horcodigo,
        horini: eventoSeleccionado.extendedProps.horini,
        horfin: eventoSeleccionado.extendedProps.horfin,
        hordiaini: eventoSeleccionado.extendedProps.hordiaini,
        hordiafin: eventoSeleccionado.extendedProps.hordiafin,
        plandescri: eventoSeleccionado.extendedProps.plandescri,
        ubicacion: {
          latitud: ubicacion.latitud,
          longitud: ubicacion.longitud,
          precision: ubicacion.precision,
          timestamp: ubicacion.timestamp,
        },
        ejecimagen: foto ? foto.base64Hex : null, // permite null para supervisores
      }

      console.log("Datos a enviar:", datosAsistencia)

      await fetchwrapper(`/EjecucionLabores/saveEjecucionEmpleado`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          ...datosAsistencia,
        }),
      })

      Swal.fire({
        title: "¡Asistencia registrada!",
        text: "Tu asistencia ha sido registrada correctamente",
        icon: "success",
        confirmButtonText: "Aceptar",
      }).then(() => {
        navigate(-1) // Volver atrás
      })
    } catch (error) {
      setEnviando(false)
      Swal.fire("Error", "No se pudo registrar la asistencia", "error")
      console.error("Error al enviar asistencia:", error)
    } finally {
      setEnviando(false)
    }
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={esLocale}>
      <ThemeProvider theme={APP_THEME}>
        <Header />
        <div className="main main-app p-3 p-lg-4">
          <BackIcon />
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              margin: "0 30px 30px 30px",
              fontSize: isMobile ? "20px" : "25px",
              textAlign: "center",
            }}
          >
            <b>Registrar Asistencia</b>
          </div>

          <CustomBackdrop isLoading={enviando || isLoadingEventos} />

          <Box sx={STYLED_ROOT}>
            {/* Indicador de pasos */}
            <Box sx={{ mb: 4 }}>
              <Stepper pasoActual={pasoActual} isMobile={isMobile} />
            </Box>

            {/* Paso 1: Selección de evento */}
            {pasoActual === 1 && (
              <Box>
                <Typography variant="h5" gutterBottom sx={{ mb: 3, textAlign: "center" }}>
                  Selecciona un evento
                </Typography>

                {eventos.length === 0 ? (
                  <Alert severity="info">No tienes eventos asignados para hoy.</Alert>
                ) : (
                  <Grid container spacing={isMobile ? 2 : 3}>
                    {eventos.map((evento) => (
                      <Grid item xs={12} key={evento.id}>
                        <EventoCard
                          evento={evento}
                          onSelect={seleccionarEvento}
                          isMobile={isMobile}
                          seleccionado={eventoSeleccionado && eventoSeleccionado.id === evento.id}
                          readonly={eventoSeleccionado && eventoSeleccionado.id === evento.id}
                        />
                      </Grid>
                    ))}
                  </Grid>
                )}
              </Box>
            )}

            {/* Paso 2: Captura de rostro */}
            {pasoActual === 2 && (
              <Box>
                <Typography variant="h5" gutterBottom sx={{ mb: 3, textAlign: "center" }}>
                  Captura tu rostro
                </Typography>

                {eventoSeleccionado && (
                  <Box sx={{ mb: 3 }}>
                    <EventoCard evento={eventoSeleccionado} isMobile={isMobile} seleccionado={true} readonly={true} />
                  </Box>
                )}

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    mb: 3,
                  }}
                >
                  <CustomPhotoCam
                    label="Foto de verificación"
                    onImage={manejarFotoCapturada}
                    cropAspect={CAMERA_CONFIG.ASPECT_RATIO}
                    compressOptions={CAMERA_CONFIG.COMPRESION}
                    showUploadButton={false}
                  />
                </Box>

                <Box
                  sx={{
                    mt: 3,
                    display: "flex",
                    justifyContent: isMobile ? "center" : "space-between",
                    flexDirection: isMobile ? "column" : "row",
                    gap: isMobile ? 2 : 0,
                  }}
                >
                  <Button variant="outlined" onClick={volverAtras} startIcon={<ArrowBack />} fullWidth={isMobile}>
                    Volver
                  </Button>

                  {/* Continuar sin foto solo para supervisores */}
                  {eventoSeleccionado?.extendedProps?.emcodemp && (
                    <Button variant="contained" onClick={saltarFoto} fullWidth={isMobile}>
                      Continuar sin foto
                    </Button>
                  )}
                </Box>
              </Box>
            )}

            {/* Paso 3: Confirmación y geolocalización */}
            {pasoActual === 3 && (
              <Box>
                <Typography variant="h5" gutterBottom sx={{ mb: 3, textAlign: "center" }}>
                  Confirmar asistencia
                </Typography>

                {eventoSeleccionado && (
                  <Box sx={{ mb: 3 }}>
                    <EventoCard evento={eventoSeleccionado} isMobile={isMobile} seleccionado={true} readonly={true} />
                  </Box>
                )}

                {/* Foto capturada - solo mostrar si existe foto */}
                {foto && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" gutterBottom sx={{ textAlign: "center" }}>
                      Foto de verificación:
                    </Typography>
                    <Box sx={{ display: "flex", justifyContent: "center" }}>
                      <Box
                        component="img"
                        src={`data:image/jpeg;base64,${foto.base64Hex}`}
                        sx={{
                          width: isMobile ? "100%" : "300px",
                          maxWidth: 300,
                          borderRadius: 2,
                          boxShadow: 3,
                        }}
                        alt="Foto capturada"
                      />
                    </Box>
                  </Box>
                )}

                {/* Mensaje para supervisores que no tomaron foto */}
                {eventoSeleccionado?.extendedProps?.emcodemp && !foto && (
                  <Alert severity="info" sx={{ mb: 3 }}>
                    Como supervisor, has continuado sin tomar foto de verificación.
                  </Alert>
                )}

                {/* Ubicación */}
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" gutterBottom sx={{ textAlign: "center" }}>
                    Ubicación:
                  </Typography>

                  {cargandoUbicacion ? (
                    <Box>
                      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", mb: 2 }}>
                        <CircularProgress size={20} sx={{ mr: 1 }} />
                        <Typography>Obteniendo ubicación precisa...</Typography>
                      </Box>
                      <LinearProgress />
                      <Typography variant="body2" sx={{ mt: 1, textAlign: "center", color: "text.secondary" }}>
                        {mejorPrecision
                          ? `Mejor precisión obtenida: ±${Math.round(mejorPrecision.precision)} metros`
                          : "Buscando señal GPS..."}
                      </Typography>
                      <Typography
                        variant="caption"
                        display="block"
                        sx={{ mt: 1, textAlign: "center", color: "text.secondary" }}
                      >
                        Para mejor precisión, asegúrate de estar en un lugar abierto con buena recepción GPS
                      </Typography>
                    </Box>
                  ) : errorUbicacion ? (
                    <Alert
                      severity="error"
                      action={
                        <Button color="inherit" size="small" onClick={obtenerUbicacion}>
                          Reintentar
                        </Button>
                      }
                    >
                      {errorUbicacion}
                    </Alert>
                  ) : ubicacion ? (
                    <>
                      <Alert
                        severity={ubicacion.precision <= GEOLOCATION_CONFIG.PRECISION_OBJETIVO ? "success" : "warning"}
                        icon={<LocationOn />}
                        sx={{ mb: 2 }}
                      >
                        {ubicacion.precision <= GEOLOCATION_CONFIG.PRECISION_OBJETIVO
                          ? "Ubicación obtenida correctamente"
                          : "Precisión de ubicación limitada"}
                        <Typography variant="body2" sx={{ mt: 1 }}>
                          Lat: {ubicacion.latitud.toFixed(6)}, Long: {ubicacion.longitud.toFixed(6)}
                          <br />
                          Precisión: ±{Math.round(ubicacion.precision)} metros
                          {ubicacion.precision > GEOLOCATION_CONFIG.PRECISION_OBJETIVO && (
                            <>
                              <br />
                              <strong>Nota:</strong> La precisión es baja. Intenta moverte a un área con mejor
                              recepción.
                            </>
                          )}
                        </Typography>
                      </Alert>

                      {/* Mapa de ubicación */}
                      <MapaUbicacion
                        latitud={ubicacion.latitud}
                        longitud={ubicacion.longitud}
                        precision={ubicacion.precision}
                        isMobile={isMobile}
                      />
                    </>
                  ) : (
                    <Box sx={{ display: "flex", justifyContent: "center" }}>
                      <Button variant="outlined" startIcon={<MyLocation />} onClick={obtenerUbicacion}>
                        Obtener ubicación
                      </Button>
                    </Box>
                  )}
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: isMobile ? "center" : "space-between",
                    flexDirection: isMobile ? "column" : "row",
                    gap: isMobile ? 2 : 0,
                  }}
                >
                  <Button variant="outlined" onClick={volverAtras} startIcon={<ArrowBack />} fullWidth={isMobile}>
                    Volver
                  </Button>

                  <Button
                    variant="contained"
                    onClick={enviarAsistencia}
                    disabled={
                      !ubicacion ||
                      enviando ||
                      (ubicacion && ubicacion.precision > GEOLOCATION_CONFIG.MAX_PRECISION_UBICACION)
                    }
                    startIcon={enviando ? <CircularProgress size={16} /> : <CheckCircle />}
                    fullWidth={isMobile}
                  >
                    {enviando ? "Enviando..." : "Confirmar asistencia"}
                  </Button>
                </Box>

                {ubicacion && ubicacion.precision > GEOLOCATION_CONFIG.MAX_PRECISION_UBICACION && (
                  <Alert severity="warning" sx={{ mt: 2 }}>
                    La precisión de la ubicación es muy baja (±{Math.round(ubicacion.precision)} metros). Por favor,
                    intenta obtener una ubicación más precisa antes de confirmar.
                  </Alert>
                )}
              </Box>
            )}
          </Box>
        </div>
      </ThemeProvider>
    </LocalizationProvider>
  )
}

export default CrearEjecucionLabores
